import type { ApolloClient } from '@apollo/client'
import { isNil } from 'lodash-es'

import { handleFetchErrorFunctional } from 'Utilities'

import { SentryErrorType } from 'Constants/errors'
import type {
  searchReturnCases,
  searchReturnCases_searchReturnCases_returnCases,
  searchReturnCasesVariables,
} from 'Constants/graphql/queries/listing/__generated__/searchReturnCases'
import { SEARCH_RETURN_CASES } from 'Constants/graphql/queries/listing/SearchReturnCases'

import type { PortalReturnLabelSelectableReturnCase } from './PortalReturnLabelModal.types'

const SEARCH_RESULT_LIMIT = 10

interface SearchPortalReturnLabelCasesArgs {
  term: string
  organisationId: string | null
  employeeId?: string | null
}

const normalizeId = (id: string | number | null | undefined): string | null =>
  isNil(id) ? null : String(id)

const toSelectableReturnCase = (
  rc: searchReturnCases_searchReturnCases_returnCases,
): PortalReturnLabelSelectableReturnCase => ({
  ...rc,
  employee: rc.employee ? { ...rc.employee, id: String(rc.employee.id) } : null,
  id: String(rc.id),
  organisationID: normalizeId(rc.organisationID),
})

export const searchPortalReturnLabelCases = async (
  client: ApolloClient<unknown>,
  { term, organisationId, employeeId }: SearchPortalReturnLabelCasesArgs,
): Promise<PortalReturnLabelSelectableReturnCase[]> => {
  const normalizedOrgId = normalizeId(organisationId)
  const trimmed = term.trim()

  if (!normalizedOrgId || trimmed.length === 0) {
    return []
  }

  try {
    const { data } = await client.query<
      searchReturnCases,
      searchReturnCasesVariables
    >({
      query: SEARCH_RETURN_CASES,
      variables: {
        employeeID: normalizeId(employeeId),
        limit: SEARCH_RESULT_LIMIT,
        organisationID: normalizedOrgId,
        searchterm: trimmed,
      },
    })

    return (data?.searchReturnCases?.returnCases ?? []).map(
      toSelectableReturnCase,
    )
  } catch (error) {
    handleFetchErrorFunctional()(
      error instanceof Error ? error : new Error('searchReturnCases failed'),
      {
        operation: {
          operationName: 'searchPortalReturnLabelCases',
          variables: { employeeId, organisationId, term: trimmed },
        },
        type: SentryErrorType.GQL_ERROR,
      },
    )

    return []
  }
}
